import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { formatDeadlineCountdown } from '@/utils/discountExpiry';
import { useDeadlineCountdown } from './useDeadlineCountdown';
import { captureDiscountCountdownShown } from './countdownAnalytics';

/**
 * Pricing page header countdown to the School Launch deadline (ORA-892 test
 * arm): the offer name and a large ticking timer, in the same glass panel
 * treatment as the trial timeline beneath it. Renders nothing once the
 * deadline passes; the page refetches the discount so a following phase gets
 * its own card.
 */
export default function DiscountCountdownCard({
  deadline,
  offerName,
  onExpired,
  className,
}: {
  deadline: Date;
  offerName: string;
  onExpired?: () => void;
  className?: string;
}) {
  const remaining = useDeadlineCountdown(deadline, onExpired);

  useEffect(() => {
    captureDiscountCountdownShown('pricing', deadline.getTime() - Date.now());
  }, [deadline]);

  if (remaining <= 0) return null;

  const formatted = formatDeadlineCountdown(remaining);

  return (
    <div
      data-testid="discount-countdown-card"
      className={cn(
        'flex flex-col items-center gap-1 rounded-2xl border border-border-subtle bg-surface-raised/60 px-6 py-4 text-center',
        className
      )}
    >
      <p className="mb-0! font-heading text-xs font-medium uppercase tracking-wide text-fg-secondary">
        {offerName} ends in
      </p>
      <p
        role="timer"
        aria-live="off"
        aria-label={`${offerName} ends in ${formatted}`}
        className="mb-0! font-heading text-3xl font-semibold tabular-nums text-fg-primary mobile:text-4xl"
      >
        {formatted}
      </p>
    </div>
  );
}
